import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { Toaster } from './components/ui/sonner';
import { Button } from './components/ui/button';
import { Heart, BookOpen, Activity, Users, MessageSquare, LogOut, Home, Menu, X, Apple, ChefHat, BookHeart, Sparkles, ExternalLink, Headphones, Smile, TrendingUp, Calendar, Search, Flame, FlaskConical, Pill, CalendarDays, Clock, Lightbulb, CreditCard, Settings, Moon, Sun, Bell, Baby, Cloud, Mic, Camera, Brain, AlertTriangle, Bug, Syringe } from 'lucide-react';
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import Dashboard from './pages/Dashboard';
import KnowledgeBase from './pages/KnowledgeBase';
import ArticleDetail from './pages/ArticleDetail';
import ConditionDetail from './pages/ConditionDetail';
import SymptomTracker from './pages/SymptomTracker';
import ChatPage from './pages/ChatPage';
import ForumPage from './pages/ForumPage';
import ForumPostDetail from './pages/ForumPostDetail';
import FoodTracker from './pages/FoodTracker';
import RecipesPage from './pages/RecipesPage';
import RecipeDetail from './pages/RecipeDetail';
import CommunityRecipesPage from './pages/CommunityRecipesPage';
import FoodEducation from './pages/FoodEducation';
import CopingLibrary from './pages/CopingLibrary';
import WhatHelped from './pages/WhatHelped';
import ResourcesPage from './pages/ResourcesPage';
import ParentRegulationTools from './pages/ParentRegulationTools';
import ChildRegulationTools from './pages/ChildRegulationTools';
import InsightsPage from './pages/InsightsPage';
import MealPlanner from './pages/MealPlanner';
import CorrelationAnalyzer from './pages/CorrelationAnalyzer';
import FlareJournal from './pages/FlareJournal';
import LabEducation from './pages/LabEducation';
import LabTracker from './pages/LabTracker';
import SupplementTracker from './pages/SupplementTracker';
import AppointmentTracker from './pages/AppointmentTracker';
import SleepTracker from './pages/SleepTracker';
import MedicalTimeline from './pages/MedicalTimeline';
import CorrelationInsights from './pages/CorrelationInsights';
import PricingPage from './pages/PricingPage';
import SubscriptionSuccessPage from './pages/SubscriptionSuccessPage';
import AdminPromoCodesPage from './pages/AdminPromoCodesPage';
import AdminStripePriceCheckPage from './pages/AdminStripePriceCheckPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import FAQPage from './pages/FAQPage';
import MedicalDisclosurePage from './pages/MedicalDisclosurePage';
import SettingsPage from './pages/SettingsPage';
import RemindersPage from './pages/RemindersPage';
import ChildProfilesPage from './pages/ChildProfilesPage';
import WeatherTracker from './pages/WeatherTracker';
import CycleTracker from './pages/CycleTracker';
import VoiceJournal from './pages/VoiceJournal';
import SymptomPhotoLog from './pages/SymptomPhotoLog';
import SmartCorrelations from './pages/SmartCorrelations';
import FlarePredictions from './pages/FlarePredictions';
import ReportIssue from './pages/ReportIssue';
import SuccessStories from './pages/SuccessStories';
import VaccineTracker from './pages/VaccineTracker';
import ResetPasswordPage from './pages/ResetPasswordPage';
import HowToUsePage from './pages/HowToUsePage';
import ContactPage from './pages/ContactPage';
import AppleLandingPage from './pages/AppleLandingPage';
import ManageTeachersPage from './pages/ManageTeachersPage';
import MyPhotosPage from './pages/MyPhotosPage';
import NewsPage from './pages/NewsPage';
import { NewsBell } from './components/NewsBell';
import TeacherAuth from './pages/TeacherAuth';
import TeacherPortal from './pages/TeacherPortal';
import TeacherReportForm from './pages/TeacherReportForm';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import SubscriptionGate from './components/SubscriptionGate';
import CookieConsentBanner from './components/CookieConsentBanner';
import MedicalDisclaimer from './components/MedicalDisclaimer';
import SidebarNavigation from './components/SidebarNavigation';
import WelcomePopup from './components/WelcomePopup';
import FirstSignInDisclaimerFlow from './components/FirstSignInDisclaimerFlow';
import '@/App.css';

// Admin emails that always have full access
const ADMIN_EMAILS = ['sfholisticapproach@gmail.com', 'diana@pandasnetwork.org', 'chelsey_sarah@hotmail.com'];

const NavigationBar = () => {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  if (!user) return null;

  const navBgClass = isDarkMode ? 'bg-[#1a1a2e] border-[#2d3a4f]' : 'bg-white border-stone-200';
  const textClass = isDarkMode ? 'text-[#e0e0e0]' : 'text-[#57534E]';

  return (
    <>
      <SidebarNavigation isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <nav className={`${navBgClass} border-b sticky top-0 z-30 shadow-sm`}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <button
              onClick={() => setSidebarOpen(true)}
              className={`p-2 rounded-lg hover:bg-stone-100 ${textClass}`}
              data-testid="hamburger-menu-button"
            >
              <Menu className="w-6 h-6" />
            </button>
            <Link to="/dashboard" className="flex items-center gap-2">
              <Heart className="w-7 h-7 text-primary fill-primary" strokeWidth={1.5} />
              <span className="text-lg font-nunito font-bold text-primary">Calm Pathways</span>
            </Link>
            <div className="flex items-center gap-2">
              <NewsBell textClass={textClass} />
              <div className={`text-sm hidden sm:block ${textClass}`}>
                Hi, {user.name?.split(' ')[0] || 'User'}
              </div>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Heart className="w-16 h-16 text-primary fill-primary mx-auto mb-4 animate-pulse" />
          <p className="text-[#A8A29E]">Loading...</p>
        </div>
      </div>
    );
  }

  return user ? children : <Navigate to="/auth" replace />;
};

// Tier-based route protection
const TierProtectedRoute = ({ children, requiredTier = 'tier1', featureName }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Heart className="w-16 h-16 text-primary fill-primary mx-auto mb-4 animate-pulse" />
          <p className="text-[#A8A29E]">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Admin users have full access
  if (ADMIN_EMAILS.includes(user.email)) {
    return children;
  }

  // Check subscription access
  const userTier = user.subscription_status || 'free';
  
  // Tier hierarchy: tier2 > tier1 > free
  const tierLevel = { 'free': 0, 'tier1': 1, 'tier2': 2 };
  const requiredLevel = tierLevel[requiredTier] || 1;
  const userLevel = tierLevel[userTier] || 0;

  if (userLevel < requiredLevel) {
    return <SubscriptionGate requiredTier={requiredTier} featureName={featureName} />;
  }

  return children;
};

// Session Timeout Warning Modal
const SessionTimeoutWarning = () => {
  const { sessionWarning, extendSession, logout } = useAuth();
  
  if (!sessionWarning) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]">
      <div className="bg-white rounded-xl p-8 max-w-md mx-4 shadow-2xl">
        <div className="flex items-center gap-3 mb-4">
          <Clock className="w-8 h-8 text-amber-500" />
          <h2 className="text-xl font-nunito font-bold text-[#2D2D2D]">
            Session Expiring Soon
          </h2>
        </div>
        <p className="text-[#57534E] mb-6">
          Your session will expire in 5 minutes due to inactivity. Would you like to stay logged in?
        </p>
        <div className="flex gap-3">
          <Button 
            onClick={extendSession}
            className="flex-1 bg-primary text-white hover:bg-primary/90"
            data-testid="extend-session-btn"
          >
            Stay Logged In
          </Button>
          <Button 
            onClick={logout}
            variant="outline"
            className="flex-1"
            data-testid="logout-now-btn"
          >
            Logout Now
          </Button>
        </div>
      </div>
    </div>
  );
};

const AppContent = () => {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const location = useLocation();

  const bgClass = isDarkMode ? 'bg-[#1a1a2e]' : 'bg-[#FAFAF9]';
  const mainBgClass = isDarkMode ? 'bg-[#16213e]' : '';

  // Pages where medical disclaimer should NOT appear
  const hideDisclaimerPaths = ['/', '/apple', '/auth', '/reset-password', '/pricing', '/privacy-policy', '/terms-of-service', '/faq', '/medical-disclosure'];
  const isTeacherPath = location.pathname.startsWith('/teacher/');
  const showDisclaimer = !hideDisclaimerPaths.includes(location.pathname) && !isTeacherPath;

  return (
    <div className={`min-h-screen ${bgClass}`}>
      {!isTeacherPath && <NavigationBar />}
      <SessionTimeoutWarning />
      {!isTeacherPath && <CookieConsentBanner />}
      {user && !isTeacherPath && <FirstSignInDisclaimerFlow />}
      {user && !isTeacherPath && location.pathname === '/dashboard' && <WelcomePopup />}
      <main className={`${user && !isTeacherPath ? 'max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8' : ''} ${mainBgClass}`}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/apple" element={<AppleLandingPage />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-of-service" element={<TermsOfServicePage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/medical-disclosure" element={<MedicalDisclosurePage />} />
          <Route path="/contact" element={<ContactPage />} />
          
          {/* Teacher Portal Routes (public - separate auth) */}
          <Route path="/teacher/login" element={<TeacherAuth />} />
          <Route path="/teacher/portal" element={<TeacherPortal />} />
          <Route path="/teacher/report/:linkId" element={<TeacherReportForm />} />
          
          {/* Admin Routes */}
          <Route
            path="/admin/promo-codes"
            element={
              <ProtectedRoute>
                <AdminPromoCodesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/stripe-price-check"
            element={
              <ProtectedRoute>
                <AdminStripePriceCheckPage />
              </ProtectedRoute>
            }
          />
          
          {/* Settings Route */}
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          
          {/* Reminders Route */}
          <Route
            path="/reminders"
            element={
              <ProtectedRoute>
                <RemindersPage />
              </ProtectedRoute>
            }
          />
          
          {/* Child Profiles Route */}
          <Route
            path="/child-profiles"
            element={
              <ProtectedRoute>
                <ChildProfilesPage />
              </ProtectedRoute>
            }
          />
          
          {/* Manage Teachers (Parent) */}
          <Route
            path="/manage-teachers"
            element={
              <TierProtectedRoute requiredTier="tier1" featureName="Teacher Observations">
                <ManageTeachersPage />
              </TierProtectedRoute>
            }
          />

          {/* My Photos — aggregated attachment gallery */}
          <Route
            path="/my-photos"
            element={
              <ProtectedRoute>
                <MyPhotosPage />
              </ProtectedRoute>
            }
          />

          {/* What's New */}
          <Route
            path="/news"
            element={
              <ProtectedRoute>
                <NewsPage />
              </ProtectedRoute>
            }
          />
          
          {/* Tier 1 Routes - Essential */}
          <Route
            path="/dashboard"
            element={
              <TierProtectedRoute requiredTier="tier1" featureName="Dashboard">
                <Dashboard />
              </TierProtectedRoute>
            }
          />
          <Route
            path="/knowledge-base"
            element={
              <TierProtectedRoute requiredTier="tier1" featureName="Knowledge Base">
                <KnowledgeBase />
              </TierProtectedRoute>
            }
          />
          <Route
            path="/knowledge-base/:id"
            element={
              <TierProtectedRoute requiredTier="tier1" featureName="Articles">
                <ArticleDetail />
              </TierProtectedRoute>
            }
          />
          <Route
            path="/condition/:conditionSlug"
            element={
              <TierProtectedRoute requiredTier="tier1" featureName="Condition Details">
                <ConditionDetail />
              </TierProtectedRoute>
            }
          />
          <Route
            path="/tracker"
            element={
              <TierProtectedRoute requiredTier="tier1" featureName="Symptom Tracker">
                <SymptomTracker />
              </TierProtectedRoute>
            }
          />
          <Route
            path="/chat"
            element={
              <TierProtectedRoute requiredTier="tier1" featureName="AI Chat">
                <ChatPage />
              </TierProtectedRoute>
            }
          />
          <Route
            path="/forum"
            element={
              <TierProtectedRoute requiredTier="tier1" featureName="Community Forum">
                <ForumPage />
              </TierProtectedRoute>
            }
          />
          <Route
            path="/forum/:id"
            element={
              <TierProtectedRoute requiredTier="tier1" featureName="Forum Post">
                <ForumPostDetail />
              </TierProtectedRoute>
            }
          />
          <Route
            path="/success-stories"
            element={
              <TierProtectedRoute requiredTier="tier1" featureName="Success Stories">
                <SuccessStories />
              </TierProtectedRoute>
            }
          />
          <Route
            path="/how-to-use"
            element={
              <TierProtectedRoute requiredTier="tier1" featureName="How to Use Guide">
                <HowToUsePage />
              </TierProtectedRoute>
            }
          />
          <Route
            path="/food-tracker"
            element={
              <TierProtectedRoute requiredTier="tier1" featureName="Food Tracker">
                <FoodTracker />
              </TierProtectedRoute>
            }
          />
          <Route
            path="/sleep-tracker"
            element={
              <TierProtectedRoute requiredTier="tier1" featureName="Sleep Tracker">
                <SleepTracker />
              </TierProtectedRoute>
            }
          />
          <Route
            path="/recipes"
            element={
              <TierProtectedRoute requiredTier="tier1" featureName="Recipes">
                <RecipesPage />
              </TierProtectedRoute>
            }
          />
          <Route
            path="/recipes/:id"
            element={
              <TierProtectedRoute requiredTier="tier1" featureName="Recipe Details">
                <RecipeDetail />
              </TierProtectedRoute>
            }
          />
          <Route
            path="/community-recipes"
            element={
              <TierProtectedRoute requiredTier="tier1" featureName="Community Recipes">
                <CommunityRecipesPage />
              </TierProtectedRoute>
            }
          />
          <Route
            path="/food-education"
            element={
              <TierProtectedRoute requiredTier="tier1" featureName="Food Education">
                <FoodEducation />
              </TierProtectedRoute>
            }
          />
          <Route
            path="/coping-library"
            element={
              <TierProtectedRoute requiredTier="tier1" featureName="Coping Library">
                <CopingLibrary />
              </TierProtectedRoute>
            }
          />
          <Route
            path="/what-helped"
            element={
              <TierProtectedRoute requiredTier="tier1" featureName="What Helped">
                <WhatHelped />
              </TierProtectedRoute>
            }
          />
          <Route
            path="/resources"
            element={
              <TierProtectedRoute requiredTier="tier1" featureName="Resources">
                <ResourcesPage />
              </TierProtectedRoute>
            }
          />
          <Route
            path="/parent-regulation"
            element={
              <TierProtectedRoute requiredTier="tier1" featureName="Parent Regulation Tools">
                <ParentRegulationTools />
              </TierProtectedRoute>
            }
          />
          <Route
            path="/child-regulation"
            element={
              <TierProtectedRoute requiredTier="tier1" featureName="Child Regulation Tools">
                <ChildRegulationTools />
              </TierProtectedRoute>
            }
          />
          <Route
            path="/flare-journal"
            element={
              <TierProtectedRoute requiredTier="tier1" featureName="Flare Journal">
                <FlareJournal />
              </TierProtectedRoute>
            }
          />
          <Route
            path="/weather-tracker"
            element={
              <TierProtectedRoute requiredTier="tier1" featureName="Weather Tracker">
                <WeatherTracker />
              </TierProtectedRoute>
            }
          />
          <Route
            path="/cycle-tracker"
            element={
              <TierProtectedRoute requiredTier="tier1" featureName="Cycle Tracker">
                <CycleTracker />
              </TierProtectedRoute>
            }
          />
          <Route
            path="/voice-journal"
            element={
              <TierProtectedRoute requiredTier="tier1" featureName="Voice Journal">
                <VoiceJournal />
              </TierProtectedRoute>
            }
          />
          <Route
            path="/report-issue"
            element={
              <ProtectedRoute>
                <ReportIssue />
              </ProtectedRoute>
            }
          />
          
          {/* Tier 2 Routes - Premium */}
          <Route
            path="/insights"
            element={
              <TierProtectedRoute requiredTier="tier2" featureName="Insights & History">
                <InsightsPage />
              </TierProtectedRoute>
            }
          />
          <Route
            path="/meal-planner"
            element={
              <TierProtectedRoute requiredTier="tier2" featureName="Meal Planner">
                <MealPlanner />
              </TierProtectedRoute>
            }
          />
          <Route
            path="/correlations"
            element={
              <TierProtectedRoute requiredTier="tier2" featureName="Food Correlations">
                <CorrelationAnalyzer />
              </TierProtectedRoute>
            }
          />
          <Route
            path="/correlation-insights"
            element={
              <TierProtectedRoute requiredTier="tier2" featureName="Correlation Insights">
                <CorrelationInsights />
              </TierProtectedRoute>
            }
          />
          <Route
            path="/lab-education"
            element={
              <TierProtectedRoute requiredTier="tier2" featureName="Lab Education">
                <LabEducation />
              </TierProtectedRoute>
            }
          />
          <Route
            path="/lab-tracker"
            element={
              <TierProtectedRoute requiredTier="tier2" featureName="Lab Tracker">
                <LabTracker />
              </TierProtectedRoute>
            }
          />
          <Route
            path="/supplements"
            element={
              <TierProtectedRoute requiredTier="tier2" featureName="Supplement Tracker">
                <SupplementTracker />
              </TierProtectedRoute>
            }
          />
          <Route
            path="/vaccines"
            element={
              <TierProtectedRoute requiredTier="tier1" featureName="Vaccine Tracker">
                <VaccineTracker />
              </TierProtectedRoute>
            }
          />
          <Route
            path="/appointments"
            element={
              <TierProtectedRoute requiredTier="tier2" featureName="Appointment Tracker">
                <AppointmentTracker />
              </TierProtectedRoute>
            }
          />
          <Route
            path="/timeline"
            element={
              <TierProtectedRoute requiredTier="tier2" featureName="Medical Timeline">
                <MedicalTimeline />
              </TierProtectedRoute>
            }
          />
          <Route
            path="/symptom-photos"
            element={
              <TierProtectedRoute requiredTier="tier2" featureName="Symptom Photo Log">
                <SymptomPhotoLog />
              </TierProtectedRoute>
            }
          />
          <Route
            path="/smart-correlations"
            element={
              <TierProtectedRoute requiredTier="tier2" featureName="Smart Correlations">
                <SmartCorrelations />
              </TierProtectedRoute>
            }
          />
          <Route
            path="/flare-predictions"
            element={
              <TierProtectedRoute requiredTier="tier2" featureName="Flare Predictions">
                <FlarePredictions />
              </TierProtectedRoute>
            }
          />
        </Routes>
      </main>
      {showDisclaimer && (
        <MedicalDisclaimer className="border-t border-gray-200 bg-gray-50 dark:bg-gray-900 dark:border-gray-800" />
      )}
      <PWAInstallPrompt />
      <Toaster position="top-center" richColors />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <NotificationProvider>
            <AppContent />
          </NotificationProvider>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
